/**
 * Подсказка «как делать правильно» — кадры, текст и атрибуция.
 *
 * Открывается внутри карточки упражнения на любом экране (миниатюра в
 * `ExerciseHead`), а не вместо неё: уходя на другой экран, человек теряет из
 * виду записанные подходы, текущий вес или несохранённую схему. Здесь же
 * достаточно закрыть карточку.
 *
 * Картинок может не быть (в свободных источниках есть не всё), связи в зале
 * тоже может не быть. Текст в обоих случаях остаётся: он едет вместе со
 * сборкой.
 *
 * Части карточки — кадры, текст и атрибуция — вынесены отдельными
 * компонентами: те же самые части показывает просмотр движения в выборе
 * упражнений, и две копии одного и того же рано или поздно разошлись бы.
 */

import { useState } from 'react'

import { TECHNIQUE, TECHNIQUE_IMAGES, TECHNIQUE_LICENSE } from '../technique'

/** Есть ли вообще что показать по этому движению. */
export function hasTechnique(exerciseId: number): boolean {
  return exerciseId in TECHNIQUE
}

/** Есть ли у движения рисунки. */
export function hasImages(exerciseId: number): boolean {
  return exerciseId in TECHNIQUE_IMAGES
}

export function Frames({ exerciseId, name }: { exerciseId: number; name: string }) {
  const images = TECHNIQUE_IMAGES[exerciseId]
  const [broken, setBroken] = useState(false)

  if (!images) return null

  if (broken) {
    // Раньше не загрузившийся рисунок убирался молча. Для карточки это
    // терпимо, а в просмотре движения, куда человек пришёл именно за
    // картинкой, пустота читается как «картинки нет вовсе». Это неправда:
    // рисунок есть, его не получилось скачать.
    return (
      <div className="faint tq-frames-hint">
        Рисунок не загрузился — без связи его не показать. Текст ниже работает всегда.
      </div>
    )
  }

  return (
    <>
      <div className="tq-frames">
        {images.frames.map((file) => (
          <img
            key={file}
            className="tq-frame"
            src={`/technique/${file}`}
            alt={`${name}: одно из крайних положений движения`}
            loading="lazy"
            onError={() => setBroken(true)}
          />
        ))}
      </div>
      {/* Подписи «начало» и «конец» тут были бы неправдой: в исходном наборе
          кадры пронумерованы, но не упорядочены. У становой тяги под первым
          номером лежит верхняя точка, у жима — нижняя. Пара показывает два
          крайних положения, а что из них старт, говорит текст ниже. */}
      <div className="faint tq-frames-hint">Два крайних положения движения</div>
      {images.note && <div className="tq-note">{images.note}</div>}
    </>
  )
}

/**
 * Кадры — или честная строка, что рисунка нет.
 *
 * Молча пропустить кадры нельзя: человек открыл подсказку тапом по «?» ради
 * картинки и должен понять, что её нет в источниках, а не что она не
 * догрузилась.
 */
export function Illustration({ exerciseId, name }: { exerciseId: number; name: string }) {
  if (hasImages(exerciseId)) return <Frames exerciseId={exerciseId} name={name} />
  return (
    <div className="faint tq-frames-hint">
      Рисунка этого движения в свободных источниках нет — ниже только текст.
    </div>
  )
}

function Block({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="tq-block">
      <div className="tq-title">{title}</div>
      <ul className="tq-list">
        {items.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  )
}

/** Текст техники: подготовка, движение, частые ошибки. */
export function TechniqueText({ exerciseId }: { exerciseId: number }) {
  const technique = TECHNIQUE[exerciseId]
  if (!technique) return null
  return (
    <>
      <Block title="Подготовка" items={technique.setup} />
      <Block title="Движение" items={technique.execution} />
      <Block title="Частые ошибки" items={technique.mistakes} />
    </>
  )
}

/**
 * Атрибуция рисунков — обязательна по лицензии CC BY-SA и стоит на виду.
 *
 * Выбор режима — не экономия места, а то, что лицензия называет «разумным
 * способом для данного носителя». Рядом с одним открытым движением — ссылка на
 * источник каждого рисунка. Под миниатюрой в карточке или под списком — автор и
 * лицензия: шестьдесят ссылок превратили бы атрибуцию в шум, который никто не
 * читает, а источники каждого рисунка лежат в просмотре, в одном тапе.
 */
export function TechniqueCredit({
  exerciseIds,
  withSources = true,
  compact = false,
}: {
  exerciseIds: number[]
  withSources?: boolean
  /** Одна строка «автор, лицензия» — под шапкой карточки. */
  compact?: boolean
}) {
  // Одно движение подряд в дне программы или в выборе — это один рисунок, и
  // ссылки на него повторяться не должны (и дали бы одинаковые ключи).
  const illustrated = [...new Set(exerciseIds)].filter((id) => id in TECHNIQUE_IMAGES)
  if (illustrated.length === 0) return null

  const showSources = withSources && !compact
  const sources = showSources
    ? [...new Set(illustrated.flatMap((id) => TECHNIQUE_IMAGES[id]?.sources ?? []))]
    : []

  return (
    <div className={`faint tq-credit${compact ? ' tq-credit-compact' : ''}`}>
      Рисунки: {TECHNIQUE_LICENSE.author},{' '}
      <a href={TECHNIQUE_LICENSE.licenseUrl} target="_blank" rel="noreferrer noopener">
        {TECHNIQUE_LICENSE.license}
      </a>
      {showSources &&
        sources.map((url, index) => (
          <span key={url}>
            {' · '}
            <a href={url} target="_blank" rel="noreferrer noopener">
              источник {index + 1}
            </a>
          </span>
        ))}
      {!withSources && !compact && ' · источник каждого рисунка — по тапу на рисунок'}
    </div>
  )
}

/** Всё содержимое подсказки: кадры, текст, атрибуция с источниками. */
export function TechniqueBody({ exerciseId, name }: { exerciseId: number; name: string }) {
  return (
    <>
      <Illustration exerciseId={exerciseId} name={name} />
      <TechniqueText exerciseId={exerciseId} />
      <TechniqueCredit exerciseIds={[exerciseId]} />
    </>
  )
}

export default function TechniqueCard({
  exerciseId,
  name,
  onClose,
  id,
}: {
  exerciseId: number
  name: string
  onClose: () => void
  id?: string
}) {
  // Хуков в этом компоненте нет и быть не должно. На тренировке экземпляр
  // переживает смену движения, а ранний return ниже поменял бы число хуков
  // между рендерами — React упал бы посреди тренировки.
  if (!hasTechnique(exerciseId)) return null

  return (
    <div className="card tight tq-card" id={id}>
      <div className="row between">
        <b>{name}: как делать</b>
        <button type="button" className="btn ghost" onClick={onClose}>
          Закрыть
        </button>
      </div>

      <TechniqueBody exerciseId={exerciseId} name={name} />
    </div>
  )
}
